<script
	lang="ts"
	context="module"
>
	export type AddressData = {
		companyName: string
		contactName: string
		street: string | null
		city: string | null
		state: string | null
		zip: string | null
		country: string | null
		email: string
		faxNumber: string
		phoneNumber: string
		mobilePhoneNumber: string
		validated: boolean
	}

	export type State = {
		country: string | null
		stateAbbreviation: string | null
		stateCode: number | null
		stateName: string | null
	}

	//end of module section
</script>

<script lang="ts">
	import { graphql } from '$houdini'

	import Input from '@isoftdata/svelte-input'
	import Select from '@isoftdata/svelte-select'
	import TextArea from '@isoftdata/svelte-textarea'

	export let addressData: AddressData
	export let states: State[] = []
	export let disableAllFields: boolean = false
	export let addressType: 'BILLING' | 'ALTERNATE' | 'VALIDATION' = 'BILLING'
	export let addressChange: (() => void) | undefined
	import type { i18n } from 'types/common'
	import { getContext } from 'svelte'
	const { t: translate } = getContext<i18n>('i18next')

	async function lookupByZipcode(zipcode: string) {
		const { data } = await autofillByZipcodeQuery.fetch({
			variables: { zipcode },
		})

		if (data?.autofillByZip) {
			addressData.city = data.autofillByZip.city
			addressData.state = data.autofillByZip.stateAbbreviation
			addressData.country = data.autofillByZip.country
		}
	}

	const autofillByZipcodeQuery = graphql(`
		query AutofillByZip($zipcode: String!) {
			autofillByZip(zipcode: $zipcode) {
				city
				country
				stateAbbreviation
			}
		}
	`)
</script>

<div class="form-row">
	<div class="col-12 col-md-6">
		<Input
			label={translate('common:companyName', 'Company Name')}
			required={!addressData.contactName && addressType === 'BILLING'}
			validation={{
				validator: value => {
					if (!value && !addressData.contactName && addressType === 'BILLING') {
						return false
					}
					return true
				},
			}}
			disabled={disableAllFields || addressType === 'VALIDATION'}
			bind:value={addressData.companyName}
			onchange={() => addressChange?.()}
		/>
	</div>
	<div class="col-12 col-md-6">
		<Input
			label={translate('common:contactName', 'Contact Name')}
			required={!addressData.companyName && addressType === 'BILLING'}
			validation={{
				validator: value => {
					if (!value && !addressData.contactName && addressType === 'BILLING') {
						return false
					}
					return true
				},
			}}
			disabled={disableAllFields || addressType === 'VALIDATION'}
			bind:value={addressData.contactName}
			onchange={() => addressChange?.()}
		/>
	</div>
	<div class="col-12 col-md-12">
		<TextArea
			label={translate('common:streetAddress', 'Street Address')}
			disabled={disableAllFields}
			bind:value={addressData.street}
			onchange={() => addressChange?.()}
		/>
	</div>
	<div class="col-12 col-md-4">
		<Input
			label={translate('common:city', 'City')}
			disabled={disableAllFields}
			bind:value={addressData.city}
			onchange={() => addressChange?.()}
		/>
	</div>
	<div class="col-12 col-md-2">
		<Select
			label={translate('common:state', 'State')}
			disabled={disableAllFields}
			bind:value={addressData.state}
			onchange={() => addressChange?.()}
		>
			{#each states as state}
				<option value={state.stateAbbreviation}>{state.stateAbbreviation}</option>
			{/each}
		</Select>
	</div>
	<div class="col-12 col-md-3">
		<Input
			label={translate('common:zipCode', 'Zip Code')}
			placeholder={translate('common:ZIPAutoFillNote', 'Autofills City, State, & Country')}
			disabled={disableAllFields}
			bind:value={addressData.zip}
			onchange={() => {
				if (!(addressData.city || addressData.state || addressData.country) && addressData.zip) {
					lookupByZipcode(addressData.zip)
				}
				addressChange?.()
			}}
		/>
	</div>
	<div class="col-12 col-md-3">
		<Input
			label={translate('common:country', 'Country')}
			disabled={disableAllFields}
			bind:value={addressData.country}
			onchange={() => addressChange?.()}
		/>
	</div>
	{#if addressType !== 'VALIDATION'}
		<hr class="w-100 mb-1" />
		<div class="col-12 col-md-6">
			<Input
				label={translate('common:email', 'Email')}
				disabled={disableAllFields}
				bind:value={addressData.email}
				onchange={() => addressChange?.()}
			/>
		</div>

		<div class="col-12 col-md-6">
			<Input
				label={translate('common:fax', 'Fax')}
				disabled={disableAllFields}
				bind:value={addressData.faxNumber}
				onchange={() => addressChange?.()}
			/>
		</div>
		<div class="col-12 col-md-6">
			<Input
				label={translate('common:phoneNumber', 'Phone Number')}
				disabled={disableAllFields}
				bind:value={addressData.phoneNumber}
				onchange={() => addressChange?.()}
			/>
		</div>
		<div class="col-12 col-md-6">
			<Input
				label={translate('common:mobileNumber', 'Mobile Number')}
				disabled={disableAllFields}
				bind:value={addressData.mobilePhoneNumber}
				onchange={() => addressChange?.()}
			/>
		</div>
		<hr class="w-100 mb-1" />
		<slot name="additionalFields" />
	{/if}
</div>
